import type { AutomationTeamDto } from '@tms/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Bot,
  CalendarClock,
  ChevronRight,
  DatabaseZap,
  KeyRound,
  Layers,
  Link2,
  MoreHorizontal,
  PlayCircle,
  PlugZap,
  Plus,
  Rocket,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import {
  Badge,
  Button,
  buttonClassName,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  Menu,
  MenuItem,
  ErrorState,
  ProgressBar,
  SearchField,
  SkeletonRows,
  Stat,
  Table,
  TableWrap,
  Td,
  Th,
  Tr,
} from '../../components/ui';
import { useErrorText, useFormat, useT } from '../../i18n';
import { usePermissions } from '../auth/permissions';
import {
  useAutomationAccess,
  useAutomationActiveRuns,
  useAutomationTeams,
  useImportFromTestRun,
} from './api';
import { useLinksFromDescriptionsAction } from './case-links/LinksFromDescriptions';
import { CatalogView } from './catalog/CatalogView';
import { Segmented } from './project/shared';
import { ConnectionDialog } from './ConnectionDialog';
import { ScheduleDialog } from './ScheduleDialog';
import { AutomationError } from './client';
import { useAutomationCan } from './hooks';
import { CreateTeamDialog } from './project/CreateTeamDialog';
import { AutomationStatusBadge, Mark } from './ui';

const TeamCard = ({ team }: { team: AutomationTeamDto }) => {
  const { t } = useT();
  const { relative } = useFormat();

  return (
    <Card>
      <Link
        to="/automation/teams/$teamId"
        params={{ teamId: team.id }}
        className="flex items-start gap-3 border-b border-border px-4 py-3 transition-colors hover:bg-sunken"
      >
        <Mark label={team.key} color={team.color} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[14px] font-semibold text-ink">{team.name}</h3>
            {team.lastRunStatus ? <AutomationStatusBadge status={team.lastRunStatus} /> : null}
          </div>
          {team.description ? (
            <p className="mt-0.5 text-[13px] text-ink-muted">{team.description}</p>
          ) : null}
          <p className="mt-1 flex flex-wrap gap-x-3 text-[12px] text-ink-subtle">
            <span>{t('automation.project.teamProjects', { count: team.projects.length })}</span>
            <span>{t('automation.projectTests', { count: team.testCount })}</span>
            <span>{t('automation.project.teamMembers', { count: team.members.length })}</span>
            {team.activeRuns > 0 ? (
              <span className="font-medium text-accent-fg">
                {t('automation.project.activeCount', { count: team.activeRuns })}
              </span>
            ) : null}
          </p>
        </div>
        <ChevronRight className="mt-1 size-4 shrink-0 text-ink-subtle" aria-hidden="true" />
      </Link>
      {team.projects.length === 0 ? (
        <p className="px-4 py-3 text-[13px] text-ink-muted">{t('automation.noProjects')}</p>
      ) : (
        <ul className="divide-y divide-border">
          {team.projects.map((project) => (
            <li key={project.id}>
              <Link
                to="/automation/teams/$teamId/projects/$projectId"
                params={{ teamId: team.id, projectId: project.id }}
                search={{}}
                className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-sunken"
              >
                <Mark label={project.key} color={project.color} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium text-ink">{project.name}</p>
                  <p className="text-[12px] text-ink-muted">
                    {t('automation.projectTests', { count: project.testCount })}
                    {' · '}
                    {project.lastRunAt
                      ? t('automation.lastRun', { when: relative(project.lastRunAt) })
                      : t('automation.neverRun')}
                  </p>
                </div>
                {project.activeRuns > 0 ? (
                  <AutomationStatusBadge status="running" />
                ) : project.lastRunStatus ? (
                  <AutomationStatusBadge status={project.lastRunStatus} />
                ) : null}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

/**
 * Takes TestRun's registry over. Repeatable until TestRun's own pages are
 * retired: each import replaces what the previous one brought.
 */
const useImportFromTestRunAction = (): { start: () => void; dialog: ReactNode } => {
  const { t } = useT();
  const { errorText } = useErrorText();
  const importRegistry = useImportFromTestRun();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const run = () =>
    importRegistry.mutate(undefined, {
      onSuccess: (summary) => {
        setConfirmOpen(false);
        toast.success(
          t('automation.importDone', {
            teams: summary.teams,
            projects: summary.projects,
            classes: summary.classes,
            runs: summary.runs,
          }),
        );
      },
      onError: (error) => {
        setConfirmOpen(false);
        toast.error(
          error instanceof AutomationError && error.status === 0
            ? t('automation.unreachable')
            : errorText(error),
        );
      },
    });

  return {
    start: () => setConfirmOpen(true),
    dialog: (
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('automation.importTitle')}
        description={t('automation.importDescription')}
        confirmLabel={t('automation.import')}
        destructive={false}
        loading={importRegistry.isPending}
        onConfirm={run}
      />
    ),
  };
};

export type AutomationView = 'catalog' | 'repos';

export const AutomationPage = ({ view = 'catalog' }: { view?: AutomationView }) => {
  const { t } = useT();
  const { relative } = useFormat();
  const { can } = usePermissions();
  const automationCan = useAutomationCan();
  const access = useAutomationAccess();
  const teams = useAutomationTeams();
  const activeRuns = useAutomationActiveRuns();
  const [createOpen, setCreateOpen] = useState(false);
  const [query, setQuery] = useState('');
  const importAction = useImportFromTestRunAction();
  const [connectionOpen, setConnectionOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const linksAction = useLinksFromDescriptionsAction();

  // the import reads TestRun's export, so it needs TestRun's address
  const canImport = can('automation:manage') && Boolean(access.data);

  // on a phone the header row has room for icons only
  const createButton = (
    <Button
      variant="primary"
      onClick={() => setCreateOpen(true)}
      aria-label={t('automation.project.home.createTeam')}
      title={t('automation.project.home.createTeam')}
    >
      <Plus className="size-4" />
      <span className="hidden sm:inline">{t('automation.project.home.createTeam')}</span>
    </Button>
  );

  const navigate = useNavigate();
  const viewSwitch = (
    <Segmented
      label={t('automation.catalog.view')}
      value={view}
      options={[
        { value: 'catalog', label: t('automation.catalog.viewCatalog') },
        { value: 'repos', label: t('automation.catalog.viewRepos') },
      ]}
      onChange={(next) =>
        void navigate({ to: '/automation', search: next === 'catalog' ? {} : { view: next } })
      }
    />
  );

  const header = (
    <PageHeader
      title={t('automation.title')}
      description={t('automation.subtitle')}
      meta={access.data ? <Badge>{t('automation.role', { role: access.data.role })}</Badge> : null}
      actions={
        <>
          {/* below lg the two links are icons, so the title keeps its room */}
          <Link
            to="/automation/accounts"
            className={buttonClassName('secondary')}
            aria-label={t('automation.project.home.accounts')}
            title={t('automation.project.home.accounts')}
          >
            <KeyRound className="size-4" />
            <span className="hidden lg:inline">{t('automation.project.home.accounts')}</span>
          </Link>
          <Link
            to="/automation/full-run"
            search={{}}
            className={buttonClassName('secondary')}
            aria-label={t('automation.project.home.fullRun')}
            title={t('automation.project.home.fullRun')}
          >
            <Layers className="size-4" />
            <span className="hidden lg:inline">{t('automation.project.home.fullRun')}</span>
          </Link>
          <Link
            to="/automation/deploys"
            className={buttonClassName('secondary')}
            aria-label={t('automation.deploys.open')}
            title={t('automation.deploys.open')}
          >
            <Rocket className="size-4" />
            <span className="hidden lg:inline">{t('automation.deploys.open')}</span>
          </Link>
          {automationCan.manage ? createButton : null}
          {/* rare admin actions: in a menu, so the header keeps room for its title */}
          {can('automation:manage') ? (
            <Menu
              trigger={
                <button
                  type="button"
                  aria-label={t('common.moreActions')}
                  className={buttonClassName('secondary', 'md', 'px-2')}
                >
                  <MoreHorizontal className="size-4" />
                </button>
              }
            >
              <MenuItem onSelect={() => setConnectionOpen(true)}>
                <PlugZap className="size-4" />
                {t('automation.connection.open')}
              </MenuItem>
              <MenuItem onSelect={() => setScheduleOpen(true)}>
                <CalendarClock className="size-4" />
                {t('automation.schedule.open')}
              </MenuItem>
              {teams.data && teams.data.length > 0 ? (
                <MenuItem onSelect={linksAction.start} disabled={linksAction.pending}>
                  <Link2 className="size-4" />
                  {t('automation.caseLinks.fromDescriptions')}
                </MenuItem>
              ) : null}
              {canImport ? (
                <MenuItem onSelect={importAction.start}>
                  <DatabaseZap className="size-4" />
                  {t('automation.import')}
                </MenuItem>
              ) : null}
            </Menu>
          ) : null}
        </>
      }
    />
  );

  const dialog = (
    <>
      {createOpen ? <CreateTeamDialog open onOpenChange={setCreateOpen} /> : null}
      {importAction.dialog}
      {linksAction.dialog}
      {connectionOpen ? <ConnectionDialog open onOpenChange={setConnectionOpen} /> : null}
      {scheduleOpen ? <ScheduleDialog open onOpenChange={setScheduleOpen} /> : null}
    </>
  );

  if (teams.error) {
    return (
      <>
        {header}
        <Card>
          <ErrorState error={teams.error} onRetry={() => void teams.refetch()} />
        </Card>
      </>
    );
  }

  if (!teams.data) {
    return (
      <>
        {header}
        <SkeletonRows rows={4} className="p-0" />
      </>
    );
  }

  if (teams.data.length === 0) {
    return (
      <>
        {header}
        <Card>
          <EmptyState
            icon={<Bot className="size-6" />}
            title={t('automation.empty')}
            description={canImport ? t('automation.emptyImportHint') : t('automation.emptyHint')}
            action={
              canImport ? (
                <Button variant="primary" onClick={importAction.start}>
                  <DatabaseZap className="size-4" />
                  {t('automation.import')}
                </Button>
              ) : undefined
            }
          />
        </Card>
        {dialog}
      </>
    );
  }

  const projectCount = teams.data.reduce((sum, team) => sum + team.projects.length, 0);
  const testCount = teams.data.reduce((sum, team) => sum + team.testCount, 0);
  const runs = activeRuns.data ?? [];
  const teamById = new Map(teams.data.map((team) => [team.id, team]));
  const needle = query.trim().toLowerCase();
  const shownTeams = needle
    ? teams.data.filter((team) => team.name.toLowerCase().includes(needle))
    : teams.data;

  if (view === 'catalog') {
    return (
      <>
        {header}
        <div>{viewSwitch}</div>
        <CatalogView />
        {dialog}
      </>
    );
  }

  return (
    <>
      {header}
      <div>{viewSwitch}</div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label={t('automation.statTeams')} value={teams.data.length} />
        <Stat label={t('automation.statProjects')} value={projectCount} />
        <Stat label={t('automation.statTests')} value={testCount} />
        <Stat
          label={t('automation.statActiveRuns')}
          value={activeRuns.isPending ? '…' : runs.length}
          valueClassName={runs.length > 0 ? 'text-accent-fg' : undefined}
        />
      </div>

      <Card>
        <CardHeader
          title={t('automation.activeRuns')}
          description={t('automation.activeRunsHint')}
        />
        {activeRuns.error ? (
          <ErrorState error={activeRuns.error} onRetry={() => void activeRuns.refetch()} />
        ) : activeRuns.isPending ? (
          <SkeletonRows rows={2} />
        ) : runs.length === 0 ? (
          <EmptyState
            icon={<PlayCircle className="size-6" />}
            title={t('automation.noActiveRuns')}
            description={t('automation.project.home.noActiveRunsHint')}
          />
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th className="w-full">{t('automation.columnProject')}</Th>
                  <Th>{t('automation.columnBranch')}</Th>
                  <Th>{t('automation.columnStartedBy')}</Th>
                  <Th className="w-48">{t('common.progress')}</Th>
                  <Th>{t('automation.columnStarted')}</Th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <Tr key={run.id}>
                    <Td>
                      <Link
                        to="/automation/runs/$runId"
                        params={{ runId: run.pipelineId }}
                        search={{ project: run.projectId }}
                        className="font-medium text-ink hover:text-accent-fg"
                      >
                        {teamById.get(run.teamId)?.name ?? run.teamId} / {run.projectName}
                      </Link>
                      <div className="mt-0.5 flex items-center gap-2">
                        <AutomationStatusBadge status={run.status} />
                        <span className="text-[12px] text-ink-subtle">{run.id}</span>
                      </div>
                    </Td>
                    <Td className="text-ink-muted">{run.branch}</Td>
                    <Td className="text-ink-muted">{run.user}</Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <ProgressBar value={run.progress} className="w-28" />
                        <span className="text-[12px] text-ink-muted">{run.progress}%</span>
                      </div>
                    </Td>
                    <Td className="text-ink-muted">{relative(run.startedAtISO)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-[14px] font-semibold text-ink">{t('automation.teams')}</h2>
            <p className="mt-0.5 text-[13px] text-ink-muted">
              {t('automation.project.home.teamsHint')}
            </p>
          </div>
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder={t('automation.project.home.searchTeams')}
            label={t('automation.project.home.searchTeamsLabel')}
            className="max-w-xs flex-none basis-full sm:basis-72"
          />
        </div>
        {shownTeams.length === 0 ? (
          <p className="py-6 text-center text-[13px] text-ink-muted">
            {t('automation.project.home.noTeamsFound', { query: query.trim() })}
          </p>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {shownTeams.map((team) => (
              <TeamCard key={team.id} team={team} />
            ))}
          </div>
        )}
      </section>

      {dialog}
    </>
  );
};
